import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container,
  Button, 
  Typography, 
  Paper, 
  IconButton, 
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Slider,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Stack,
  Chip,
  InputAdornment,
  AppBar,
  Toolbar,
  Slide,
  Menu
} from '@mui/material';
import Draggable from 'react-draggable';
import { 
  Add as AddIcon, 
  Save as SaveIcon, 
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  Edit as EditIcon,
  RestartAlt as RestartAltIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  CalendarToday as CalendarIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  FormatBold as BoldIcon,
  FormatItalic as ItalicIcon,
  Print as PrintIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { supabase } from '../../supabaseClient';
import { billingService } from '../../services/billingService';
import { customerService } from '../../services/customerService';

// Initialize PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

const WATER_BILLING_FIELDS = [
  { key: 'account_name', label: 'Account Name' },
  { key: 'account_no', label: 'Account No.' },
  { key: 'meter_no', label: 'Meter No.' },
  { key: 'address', label: 'Address' },
  { key: 'previous_reading', label: 'Previous Reading' },
  { key: 'sequence_no', label: 'Sequence No.' },
  { key: 'barangay', label: 'Barangay' },
  { key: 'period', label: 'Billing Period' },
  { key: 'period_covered', label: 'Period Covered' },
  { key: 'due_date', label: 'Due Date' },
  { key: 'customer_id', label: 'Customer ID' },
  { key: 'customer_name', label: 'Customer Name' },
  { key: 'customer_type', label: 'Customer Type' },
  { key: 'status', label: 'Status' },
  { key: 'current_reading', label: 'Current Reading' },
  { key: 'consumption', label: 'Consumption' },
  { key: 'billed_month', label: 'Billed Month' },
  { key: 'reader_name', label: 'Reader Name' }
];

const sanitizeText = (text) => {
  if (text === null || text === undefined) return '';
  let str = String(text);
  str = str.replace(/ñ/g, 'n').replace(/Ñ/g, 'N');
  return str.replace(/[^\x20-\x7E]/g, '');
};

const formatMonthYear = (value) => {
  if (!value) return '';
  const str = String(value);
  let date;
  if (/^\d{4}-\d{2}$/.test(str)) {
    date = new Date(`${str}-01`);
  } else {
    date = new Date(str);
  }
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'long'
  });
};

const getPeriodCovered = (billedMonth) => {
  if (!billedMonth) return '';
  const str = String(billedMonth);
  let date;
  if (/^\d{4}-\d{2}$/.test(str)) {
    date = new Date(`${str}-01`);
  } else {
    date = new Date(str);
  }
  if (Number.isNaN(date.getTime())) return '';
  date.setMonth(date.getMonth() + 1);
  return date.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'long'
  });
};

const getDueDate = (billedMonth) => {
  if (!billedMonth) return '';
  const str = String(billedMonth);
  let date;
  if (/^\d{4}-\d{2}$/.test(str)) {
    date = new Date(`${str}-01`);
  } else {
    date = new Date(str);
  }
  if (Number.isNaN(date.getTime())) return '';
  date.setMonth(date.getMonth() + 2);
  date.setDate(20);
  return date.toLocaleDateString('en-PH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// --- Template Manager Component ---
const ReadingSheetTemplateManager = ({ onClose }) => {
  const [step, setStep] = useState('select-template'); // select-template, mapping
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [existingTemplate, setExistingTemplate] = useState(null);
  const [pdfImage, setPdfImage] = useState(null);
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 }); 
  const [fields, setFields] = useState([]);
  const [fontSize, setFontSize] = useState(12);
  const [rowHeight, setRowHeight] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [customLabel, setCustomLabel] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

  useEffect(() => {
    fetchTemplate();
  }, []);

  const fetchTemplate = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('water_reading_templates')
        .select('*')
        .eq('template_type', 'standard')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setExistingTemplate(data);
      }
    } catch (err) {
      console.error('Error fetching template:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    try {
      const existingMappings = existingTemplate?.mappings || {};
      let fileToUpload = file;
      let fileName = `reading_sheet_template_${Date.now()}.pdf`;

      // Convert Image to PDF if necessary
      if (file.type.startsWith('image/')) {
        const imageBytes = await file.arrayBuffer();
        const pdfDoc = await PDFDocument.create();
        
        let image;
        if (file.type === 'image/jpeg') {
            image = await pdfDoc.embedJpg(imageBytes);
        } else {
            image = await pdfDoc.embedPng(imageBytes);
        }

        const page = pdfDoc.addPage([image.width, image.height]);
        page.drawImage(image, {
            x: 0,
            y: 0,
            width: image.width,
            height: image.height,
        });

        const pdfBytes = await pdfDoc.save();
        fileToUpload = new Blob([pdfBytes], { type: 'application/pdf' });
      }
      
      // Upload to Storage
      const { error: uploadError } = await supabase.storage
        .from('water-billing-templates')
        .upload(fileName, fileToUpload);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('water-billing-templates')
        .getPublicUrl(fileName);

      // Save/Update DB
      const { data: dbData, error: dbError } = await supabase
        .from('water_reading_templates')
        .upsert({
          template_type: 'standard', // Single standard template for now
          file_url: publicUrl,
          updated_at: new Date(),
          mappings: existingMappings
        }, { onConflict: 'template_type' })
        .select()
        .single();

      if (dbError) throw dbError;

      setExistingTemplate(dbData);
      await loadTemplateForMapping(fileToUpload, existingMappings);
    } catch (err) {
      console.error('Error uploading template:', err);
      setError('Failed to upload template: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUseExisting = async () => {
    if (!existingTemplate?.file_url) return;
    setLoading(true);
    try {
      const response = await fetch(existingTemplate.file_url);
      const blob = await response.blob();
      await loadTemplateForMapping(blob, existingTemplate.mappings);
    } catch (err) {
      console.error('Error loading existing template:', err);
      setError('Failed to load existing template.');
      setLoading(false);
    }
  };

  const loadTemplateForMapping = async (fileBlob, savedMappings) => {
    try {
      await renderPdfToImage(fileBlob);
      
      if (savedMappings) {
        if (Array.isArray(savedMappings)) {
          setFields(savedMappings);
        } else {
          const loadedFields = [];

          if (savedMappings._config) {
            setRowHeight(savedMappings._config.rowHeight || 0);
            setRowsPerPage(savedMappings._config.rowsPerPage || 15);
          }

          Object.entries(savedMappings).forEach(([key, value]) => {
            if (key === '_config') return;

          if (key === '_custom' && Array.isArray(value)) {
            value.forEach(item => {
              loadedFields.push({
                id: `custom_${makeId()}`,
                type: 'custom',
                label: item?.label || '',
                value: item?.value || '',
                x: Math.max(0, item?.uiX || 50),
                y: Math.max(0, item?.uiY || 50),
                width: item?.uiWidth || 150,
                alignment: item?.alignment || 'left',
                fontSize: item?.size || fontSize,
                color: item?.color || '#000000',
                bold: !!item?.bold,
                italic: !!item?.italic,
                fontFamily: item?.fontFamily || 'tahoma'
              });
            });
            return;
          }

            if (Array.isArray(value)) {
              value.forEach(pos => {
                loadedFields.push({
                  id: `${key}_${makeId()}`,
                  type: 'mapped',
                  key,
                  x: Math.max(0, pos?.uiX || 50),
                  y: Math.max(0, pos?.uiY || 50),
                  width: pos?.uiWidth || 150,
                  alignment: pos?.alignment || 'left',
                  fontSize: pos?.size || fontSize,
                  color: pos?.color || '#000000',
                  bold: !!pos?.bold,
                  italic: !!pos?.italic,
                  fontFamily: pos?.fontFamily || 'tahoma'
                });
              });
              return;
            }

            if (value && typeof value === 'object') {
              loadedFields.push({
                id: `${key}_${makeId()}`,
                type: 'mapped',
                key,
                x: Math.max(0, value.uiX || 50),
                y: Math.max(0, value.uiY || 50),
                width: value.uiWidth || 150,
                alignment: value.alignment || 'left',
                fontSize: value.size || fontSize,
                color: value.color || '#000000',
                bold: !!value.bold,
                italic: !!value.italic,
                fontFamily: value.fontFamily || 'tahoma'
              });
            }
          });

          const firstMapped = Object.entries(savedMappings).find(([k, v]) => k !== '_config' && k !== '_custom' && v && typeof v === 'object');
          if (firstMapped) {
            const v = firstMapped[1];
            const sample = Array.isArray(v) ? v[0] : v;
            if (sample?.size) setFontSize(sample.size);
          }

          setFields(loadedFields);
        }
      } else {
        setFields([]);
      }

      setStep('mapping');
    } catch (err) {
      console.error('Error rendering PDF:', err);
      setError('Failed to render PDF.');
    } finally {
      setLoading(false);
    }
  };

  const renderPdfToImage = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument(arrayBuffer);
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);

    const viewport = page.getViewport({ scale: 1.5 });
    setPdfDimensions({ width: viewport.width / 1.5, height: viewport.height / 1.5 });

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;

    await page.render({ canvasContext: context, viewport: viewport }).promise;
    setPdfImage(canvas.toDataURL());
  };

  const handleAddField = (fieldKey) => {
    setFields(prev => [
      ...prev,
      {
        id: `${fieldKey}_${makeId()}`,
        type: 'mapped',
        key: fieldKey,
        x: 50,
        y: 50,
        width: 150,
        alignment: 'left',
        fontSize,
        color: '#000000',
        bold: false,
        italic: false,
        fontFamily: 'tahoma'
      }
    ]);
  };

  const handleRemoveField = (id) => {
    setFields(prev => prev.filter(f => f.id !== id));
  };

  const handleAlignmentChange = (id, newAlignment) => {
    setFields(prev => prev.map(f => (f.id === id ? { ...f, alignment: newAlignment } : f)));
  };

  const handleDragStop = (id, e, data) => {
    setFields(prev => prev.map(f => {
      if (f.id === id) {
        return { ...f, x: data.x, y: data.y };
      }
      return f;
    }));
  };

  const handleFieldStyleChange = (id, changes) => {
    setFields(prev => prev.map(f => {
      if (f.id !== id) return f;
      const next = { ...f, ...changes };

      if (Object.prototype.hasOwnProperty.call(changes, 'fontSize')) {
        const size = changes.fontSize || fontSize;
        const key = f.type === 'custom' ? '' : (f.key || f.id);
        const labelText = f.type === 'custom'
          ? (f.label || '')
          : (WATER_BILLING_FIELDS.find(af => af.key === key)?.label || key || '');
        const length = Math.max(labelText.length, 4);
        const baseWidth = length * size * 0.6;
        const clampedWidth = Math.max(60, Math.min(baseWidth, 500));
        next.width = clampedWidth;
      }

      return next;
    }));
  };

  const handleSaveMapping = async () => {
    if (!imgRef.current || !pdfDimensions.width) return;

    setLoading(true);
    try {
      const containerW = imgRef.current.offsetWidth;
      const containerH = imgRef.current.offsetHeight;
      const scaleX = pdfDimensions.width / containerW;
      const scaleY = pdfDimensions.height / containerH;

      const mapping = {
        _config: {
          rowHeight,
          rowsPerPage
        }
      };
      const custom = [];

      fields.forEach(field => {
        const safeX = Math.max(0, field.x);
        const safeY = Math.max(0, field.y);
        const fieldFontSize = field.fontSize || fontSize;

        const pdfX = safeX * scaleX;
        const pdfY = pdfDimensions.height - (safeY * scaleY) - fieldFontSize;

        const entry = {
          x: pdfX,
          y: pdfY,
          size: fieldFontSize,
          uiX: safeX,
          uiY: safeY,
          alignment: field.alignment || 'left',
          uiWidth: field.width || 150,
          width: (field.width || 150) * scaleX,
          color: field.color || '#000000',
          bold: !!field.bold,
          italic: !!field.italic,
          fontFamily: field.fontFamily || 'tahoma'
        };

        if (field.type === 'custom') {
          custom.push({
            label: field.label || '',
            value: field.value || '',
            ...entry
          });
        } else {
          const key = field.key || field.id;
          if (!mapping[key]) mapping[key] = [];
          mapping[key].push(entry);
        }
      });

      if (custom.length > 0) {
        mapping._custom = custom;
      }

      const { error } = await supabase
        .from('water_reading_templates')
        .update({
          mappings: mapping
        })
        .eq('template_type', 'standard');

      if (error) throw error;

      setSuccess('Mapping saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
      fetchTemplate();
    } catch (err) {
      console.error('Error saving mapping:', err);
      setError('Failed to save mapping.');
    } finally {
      setLoading(false);
    }
  };

  const selectedField = fields.find(f => f.id === selectedFieldId) || null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Manage Reading Sheet Template
            </Typography>
            <Button autoFocus color="inherit" onClick={onClose}>
              Done
            </Button>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            {step === 'select-template' && (
                <Grid container spacing={3} justifyContent="center">
                    <Grid item xs={12} md={6}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3, border: '1px dashed grey' }}>
                            <Typography variant="h6" gutterBottom>Current Template</Typography>
                            {loading ? (
                                <CircularProgress />
                            ) : existingTemplate ? (
                                <Box sx={{ textAlign: 'center', width: '100%' }}>
                                    <DescriptionIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                                    <Typography variant="body1" gutterBottom>Standard Reading Sheet</Typography>
                                    <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                                        Last updated: {new Date(existingTemplate.updated_at).toLocaleDateString()}
                                    </Typography>
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                                        <Button variant="contained" startIcon={<EditIcon />} onClick={handleUseExisting}>
                                            Edit Mapping
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary" paragraph>
                                        No template uploaded yet.
                                    </Typography>
                                </Box>
                            )}
                            
                            <Box sx={{ mt: 3, width: '100%' }}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    fullWidth
                                    startIcon={<CloudUploadIcon />}
                                >
                                    {existingTemplate ? 'Replace Template' : 'Upload Template'}
                                    <input
                                        type="file"
                                        hidden
                                        accept="application/pdf,image/png,image/jpeg"
                                        onChange={handleUpload}
                                    />
                                </Button>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {step === 'mapping' && (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => setStep('select-template')}>
                            Back to Selection
                        </Button>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveMapping}>
                            Save Mapping
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flex: 1, gap: 2, minHeight: 0, position: 'relative' }}>
                        {/* Sidebar */}
                        <Paper sx={{ width: 280, p: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Fields</Typography>
                                <IconButton size="small" onClick={() => {
                                    if (window.confirm('Reset all fields?')) {
                                        setFields([]);
                                        setSelectedFieldId(null);
                                    }
                                }}>
                                    <RestartAltIcon />
                                </IconButton>
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="caption">Font Size: {fontSize}px</Typography>
                                <Slider
                                    value={fontSize}
                                    onChange={(e, v) => setFontSize(v)}
                                    min={8}
                                    max={24}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    size="small"
                                />
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold">List Settings</Typography>
                                <Typography variant="caption">Row Height (px): {rowHeight}</Typography>
                                <Slider
                                    value={rowHeight}
                                    onChange={(e, v) => setRowHeight(v)}
                                    min={0}
                                    max={100}
                                    step={1}
                                    valueLabelDisplay="auto"
                                    size="small"
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    Set > 0 to enable list mode
                                </Typography>
                                
                                {rowHeight > 0 && (
                                    <>
                                        <Typography variant="caption">Rows Per Page: {rowsPerPage}</Typography>
                                        <Slider
                                            value={rowsPerPage}
                                            onChange={(e, v) => setRowsPerPage(v)}
                                            min={1}
                                            max={50}
                                            step={1}
                                            valueLabelDisplay="auto"
                                            size="small"
                                        />
                                    </>
                                )}
                            </Box>

                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" fontWeight="bold">Custom Field</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Label"
                                    value={customLabel}
                                    onChange={(e) => setCustomLabel(e.target.value)}
                                    sx={{ mb: 1 }}
                                />
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Value"
                                    value={customValue}
                                    onChange={(e) => setCustomValue(e.target.value)}
                                    sx={{ mb: 1 }}
                                />
                                <Button
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        const label = customLabel.trim();
                                        const value = customValue;
                                        if (!label && !value) return;
                                        setFields(prev => ([
                                            ...prev,
                                            {
                                                id: `custom_${makeId()}`,
                                                type: 'custom',
                                                label,
                                                value,
                                                x: 50,
                                                y: 50,
                                                width: 150,
                                                alignment: 'left',
                                                fontSize,
                                                color: '#000000',
                                                bold: false,
                                                italic: false
                                            }
                                        ]));
                                        setCustomLabel('');
                                        setCustomValue('');
                                    }}
                                    disabled={!customLabel.trim() && !customValue}
                                >
                                    Add Custom
                                </Button>
                            </Box>

                            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                                {WATER_BILLING_FIELDS.map(field => {
                                    const isUsed = fields.some(f => f.id === field.key);
                                    return (
                                        <Button
                                            key={field.key}
                                            variant={isUsed ? "contained" : "outlined"}
                                            color={isUsed ? "success" : "primary"}
                                            fullWidth
                                            size="small"
                                            sx={{ mb: 1, justifyContent: 'flex-start', textAlign: 'left' }}
                                            onClick={() => !isUsed && handleAddField(field.key)}
                                            startIcon={isUsed ? <DeleteIcon onClick={(e) => { e.stopPropagation(); handleRemoveField(field.key); }}/> : <AddIcon />}
                                        >
                                            {field.label}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Paper>

                        {/* Canvas */}
                        <Paper 
                            sx={{ flex: 1, bgcolor: '#f5f5f5', overflow: 'auto', p: 2, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
                            onClick={() => setSelectedFieldId(null)}
                        >
                            {loading && <CircularProgress />}
                            {!loading && pdfImage && (
                                <div 
                                    ref={containerRef}
                                    style={{ 
                                        position: 'relative', 
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                                        display: 'inline-block',
                                        bgcolor: 'white'
                                    }}
                                >
                                    <img 
                                        ref={imgRef}
                                        src={pdfImage} 
                                        alt="Template" 
                                        style={{ display: 'block', maxWidth: '100%', maxHeight: 'none' }} 
                                        draggable={false}
                                    />
                                    
                                    {fields.map(field => {
                                        const nodeRef = React.createRef();
                                        const isCustom = field.type === 'custom';
                                        const fieldKey = isCustom ? '' : (field.key || field.id);
                                        const label = isCustom
                                            ? (field.label || 'Custom')
                                            : (WATER_BILLING_FIELDS.find(af => af.key === fieldKey)?.label || fieldKey);
                                        return (
                                            <Draggable
                                                key={field.id}
                                                nodeRef={nodeRef}
                                                defaultPosition={{ x: field.x, y: field.y }}
                                                onStop={(e, data) => handleDragStop(field.id, e, data)}
                                            >
                                                <div
                                                    ref={nodeRef}
                                                    onClick={(e) => { e.stopPropagation(); setSelectedFieldId(field.id); }}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: field.width || 150,
                                                        cursor: 'move',
                                                        backgroundColor: selectedFieldId === field.id ? 'rgba(59, 130, 246, 0.35)' : 'rgba(59, 130, 246, 0.2)',
                                                        border: selectedFieldId === field.id ? '2px solid #1d4ed8' : '1px dashed #3b82f6',
                                                        padding: '2px 4px',
                                                        fontSize: `${field.fontSize || fontSize}px`,
                                                        fontWeight: field.bold ? 'bold' : 'normal',
                                                        fontStyle: field.italic ? 'italic' : 'normal',
                                                        whiteSpace: 'nowrap',
                                                        color: field.color || '#1e40af',
                                                        zIndex: selectedFieldId === field.id ? 20 : 10,
                                                        userSelect: 'none',
                                                        textAlign: field.alignment || 'left',
                                                        boxShadow: selectedFieldId === field.id ? '0 0 0 1px rgba(37,99,235,0.5)' : 'none'
                                                    }}
                                                    title={`x: ${field.x}, y: ${field.y}`}
                                                >
                                                    <div style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {label}
                                                    </div>
                                                    {isCustom && field.value !== undefined && field.value !== '' && (
                                                        <span style={{ fontSize: '10px' }}>
                                                            {String(field.value)}
                                                            <br />
                                                        </span>
                                                    )}
                                                    <span style={{ fontSize: '9px' }}>({Math.round(field.x)}, {Math.round(field.y)})</span>
                                                </div>
                                            </Draggable>
                                        );
                                    })}
                                </div>
                            )}
                        </Paper>

                        {selectedField && (
                            <Paper
                                elevation={4}
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    p: 1.5,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255,255,255,0.95)'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                        {selectedField.type === 'custom'
                                            ? (selectedField.label || 'Custom')
                                            : (WATER_BILLING_FIELDS.find(af => af.key === (selectedField.key || selectedField.id))?.label || (selectedField.key || selectedField.id))}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ({Math.round(selectedField.x)}, {Math.round(selectedField.y)})
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AlignLeftIcon
                                        fontSize="small"
                                        style={{ cursor: 'pointer', color: (selectedField.alignment || 'left') === 'left' ? '#1d4ed8' : '#9ca3af' }}
                                        onClick={() => handleAlignmentChange(selectedField.id, 'left')}
                                    />
                                    <AlignCenterIcon
                                        fontSize="small"
                                        style={{ cursor: 'pointer', color: selectedField.alignment === 'center' ? '#1d4ed8' : '#9ca3af' }}
                                        onClick={() => handleAlignmentChange(selectedField.id, 'center')}
                                    />
                                    <AlignRightIcon
                                        fontSize="small"
                                        style={{ cursor: 'pointer', color: selectedField.alignment === 'right' ? '#1d4ed8' : '#9ca3af' }}
                                        onClick={() => handleAlignmentChange(selectedField.id, 'right')}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <BoldIcon
                                        fontSize="small"
                                        style={{ cursor: 'pointer', color: selectedField.bold ? '#1d4ed8' : '#9ca3af' }}
                                        onClick={() => handleFieldStyleChange(selectedField.id, { bold: !selectedField.bold })}
                                    />
                                    <ItalicIcon
                                        fontSize="small"
                                        style={{ cursor: 'pointer', color: selectedField.italic ? '#1d4ed8' : '#9ca3af' }}
                                        onClick={() => handleFieldStyleChange(selectedField.id, { italic: !selectedField.italic })}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Select
                                        size="small"
                                        value={selectedField.fontFamily || 'tahoma'}
                                        onChange={(e) => {
                                            const value = e.target.value || 'tahoma';
                                            handleFieldStyleChange(selectedField.id, { fontFamily: value });
                                        }}
                                        sx={{ fontSize: 12, height: 30 }}
                                    >
                                        <MenuItem value="tahoma">Tahoma (Sans)</MenuItem>
                                        <MenuItem value="times">Times New Roman (Serif)</MenuItem>
                                        <MenuItem value="courier">Courier New (Mono)</MenuItem>
                                        <MenuItem value="vivaldi">Vivaldi (Script-like)</MenuItem>
                                    </Select>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <input
                                        type="number"
                                        min={6}
                                        max={24}
                                        value={selectedField.fontSize || fontSize}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value, 10);
                                            if (!isNaN(val)) {
                                                handleFieldStyleChange(selectedField.id, { fontSize: val });
                                            }
                                        }}
                                        style={{
                                            width: 50,
                                            fontSize: 12,
                                            padding: '2px 4px'
                                        }}
                                    />
                                    <input
                                        type="color"
                                        value={selectedField.color || '#000000'}
                                        onChange={(e) => {
                                            handleFieldStyleChange(selectedField.id, { color: e.target.value || '#000000' });
                                        }}
                                        style={{
                                            width: 28,
                                            height: 22,
                                            padding: 0,
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer'
                                        }}
                                    />
                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        handleRemoveField(selectedField.id);
                                        setSelectedFieldId(null);
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Paper>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    </Box>
  );
};

// --- Main Page Component ---
const ReadingSheetsPage = () => {
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  
  const [selectedMonth, setSelectedMonth] = useState('');
  const [readingData, setReadingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBarangay, setFilterBarangay] = useState('all');
  const [filterType, setFilterType] = useState('ResidentialAndCommercial');
  const [filterStatus, setFilterStatus] = useState('Active');
  const [barangays, setBarangays] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);

  const [generating, setGenerating] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [approverDialogOpen, setApproverDialogOpen] = useState(false);
  const [approverUsers, setApproverUsers] = useState([]);
  const [selectedApproverId, setSelectedApproverId] = useState('');
  const [approverLoading, setApproverLoading] = useState(false);

  const loadApproverUsers = async () => {
    try {
      setApproverLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('userid, firstname, lastname, position, status, signature_url')
        .eq('status', 'active')
        .order('lastname', { ascending: true });
      if (error) throw error;
      setApproverUsers(data || []);
      if ((data || []).length > 0 && !selectedApproverId) {
        setSelectedApproverId(data[0].userid);
      }
    } catch (err) {
      console.error('Error loading approvers:', err);
    } finally {
      setApproverLoading(false);
    }
  };

  const handlePrintMenuOpen = (event) => {
      setPrintMenuAnchor(event.currentTarget);
  };

  const handlePrintMenuClose = () => {
      setPrintMenuAnchor(null);
  };

  const handleCustomPrint = () => {
      handlePrintMenuClose();
      handleGenerateReadingSheets();
  };

  const handleDownloadStandard = async () => {
      handlePrintMenuClose();
      setApproverDialogOpen(true);
      if (approverUsers.length === 0) {
        await loadApproverUsers();
      }
  };

  // Initialize current month
  useEffect(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${year}-${month}`);
    loadFilterOptions();
  }, []);

  // Load reading data when month changes
  useEffect(() => {
    if (selectedMonth) {
      loadReadingData();
    }
  }, [selectedMonth]);

  const loadFilterOptions = async () => {
    try {
      const [barangayResult, typeResult] = await Promise.all([
        customerService.getBarangays(),
        customerService.getCustomerTypes()
      ]);
      
      if (barangayResult.success) {
        setBarangays(barangayResult.data || []);
      }
      
      if (typeResult.success) {
        setCustomerTypes(typeResult.data || []);
      }
    } catch (err) {
      console.error('Error loading filter options:', err);
    }
  };
  
  const loadReadingData = async () => {
    if (!selectedMonth) return;
    
    setLoading(true);
    try {
      const result = await billingService.getBillsByMonth(selectedMonth);
      if (result.success) {
        setReadingData(result.data || []);
      }
    } catch (err) {
      console.error('Error loading reading data:', err);
    }
    setLoading(false);
  };

  const filteredData = readingData.filter(item => {
    const matchesSearch = !searchTerm || 
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(item.customerid).includes(searchTerm);
    
    const matchesBarangay = filterBarangay === 'all' || 
      item.barangay?.toLowerCase() === filterBarangay.toLowerCase();

    const typeValue = (item.type || '').toLowerCase();
    const matchesType =
      filterType === 'all'
        ? true
        : filterType === 'ResidentialAndCommercial'
          ? typeValue === 'residential' || typeValue === 'commercial'
          : typeValue === filterType.toLowerCase();

    const statusValue = (item.status || '').toLowerCase();
    const matchesStatus =
      filterStatus === 'all'
        ? true
        : statusValue === filterStatus.toLowerCase();
    
    return matchesSearch && matchesBarangay && matchesType && matchesStatus;
  });

  const sortedData = [...filteredData].sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    // Handle numeric comparisons
    const numericKeys = ['customerid', 'previousreading', 'currentreading', 'consumption'];
    if (numericKeys.includes(sortConfig.key)) {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
    } else {
        // String comparison
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const generatePDFBlob = async () => {
    try {
        // 1. Fetch Template
        const { data: templateData, error: templateError } = await supabase
            .from('water_reading_templates')
            .select('*')
            .eq('template_type', 'standard')
            .maybeSingle();

        if (templateError || !templateData || !templateData.file_url) {
            throw new Error('Template not found. Please upload and map a template first.');
        }

        const mappings = templateData.mappings;
        if (!mappings || Object.keys(mappings).length === 0) {
             throw new Error('Template has no mappings. Please edit the template and add fields.');
        }

        // 2. Load PDF Template
        const templateBytes = await fetch(templateData.file_url).then(res => res.arrayBuffer());
        
        const pdfDoc = await PDFDocument.create();
        const templatePdf = await PDFDocument.load(templateBytes);
        const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
        const fontBoldItalic = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);
        const fontTimes = await pdfDoc.embedFont(StandardFonts.TimesRoman);
        const fontTimesBold = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);
        const fontTimesItalic = await pdfDoc.embedFont(StandardFonts.TimesRomanItalic);
        const fontTimesBoldItalic = await pdfDoc.embedFont(StandardFonts.TimesRomanBoldItalic);
        const fontCourier = await pdfDoc.embedFont(StandardFonts.Courier);
        const fontCourierBold = await pdfDoc.embedFont(StandardFonts.CourierBold);
        const fontCourierOblique = await pdfDoc.embedFont(StandardFonts.CourierOblique);
        const fontCourierBoldOblique = await pdfDoc.embedFont(StandardFonts.CourierBoldOblique);

        const rowHeight = mappings._config?.rowHeight || 0;
        const rowsPerPage = mappings._config?.rowsPerPage || 15;

        let assignedReader = '';
        if (filterBarangay && filterBarangay !== 'all' && Array.isArray(barangays) && barangays.length > 0) {
            const barangayMatch = barangays.find(b =>
                (b.barangay || '').toLowerCase() === filterBarangay.toLowerCase()
            );
            assignedReader = (barangayMatch?.reader || '').trim();
        }

        const parseColor = (hex) => {
            if (!hex || typeof hex !== 'string' || hex[0] !== '#' || hex.length !== 7) {
                return rgb(0, 0, 0);
            }
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
                return rgb(0, 0, 0);
            }
            return rgb(r / 255, g / 255, b / 255);
        };

        const getFontForEntry = (entry) => {
            const rawFamily = entry.fontFamily || 'tahoma';
            const family = String(rawFamily).toLowerCase();
            const isBold = !!entry.bold;
            const isItalic = !!entry.italic;

            if (family === 'times' || family === 'serif') {
                if (isBold && isItalic) return fontTimesBoldItalic;
                if (isBold) return fontTimesBold;
                if (isItalic) return fontTimesItalic;
                return fontTimes;
            }

            if (family === 'courier' || family === 'mono') {
                if (isBold && isItalic) return fontCourierBoldOblique;
                if (isBold) return fontCourierBold;
                if (isItalic) return fontCourierOblique;
                return fontCourier;
            }

            if (family === 'vivaldi' || family === 'script') {
                if (isBold && isItalic) return fontTimesBoldItalic;
                if (isBold) return fontTimesBold;
                return fontTimesItalic;
            }

            if (family === 'sans' || family === 'tahoma' || !family) {
                if (isBold && isItalic) return fontBoldItalic;
                if (isBold) return fontBold;
                if (isItalic) return fontItalic;
                return fontRegular;
            }

            if (isBold && isItalic) return fontBoldItalic;
            if (isBold) return fontBold;
            if (isItalic) return fontItalic;
            return fontRegular;
        };

        const drawCustomFields = (page) => {
            const customFields = mappings._custom;
            if (!Array.isArray(customFields)) return;
            customFields.forEach(entry => {
                let rawText = entry.value ?? '';

                if (filterBarangay && filterBarangay !== 'all' && Array.isArray(barangays) && barangays.length > 0) {
                    const barangayMatch = barangays.find(b => 
                        (b.barangay || '').toLowerCase() === filterBarangay.toLowerCase()
                    );
                    const assignedReader = (barangayMatch?.reader || '').trim();

                    if (assignedReader) {
                        const labelLower = String(entry.label || '').toLowerCase();
                        const valueLower = String(entry.value || '').toLowerCase();

                        if (
                            labelLower === 'read by' ||
                            labelLower === 'read-by' ||
                            labelLower === 'reader' ||
                            labelLower.includes('read by') ||
                            valueLower.includes('read by') ||
                            valueLower === 'reader'
                        ) {
                            rawText = assignedReader;
                        }
                    }
                }

                const text = sanitizeText(rawText);
                if (!text || entry.x === undefined || entry.y === undefined) return;
                const size = entry.size || 12;
                const color = parseColor(entry.color);
                const font = getFontForEntry(entry);
                let drawX = entry.x;
                const width = entry.width;
                if (width && entry.alignment && font) {
                    const textWidth = font.widthOfTextAtSize(text, size);
                    if (entry.alignment === 'center') {
                        drawX = entry.x + (width - textWidth) / 2;
                    } else if (entry.alignment === 'right') {
                        drawX = entry.x + (width - textWidth);
                    }
                }
                page.drawText(text, {
                    x: drawX,
                    y: entry.y,
                    size,
                    font,
                    color
                });
            });
        };

        if (rowHeight > 0) {
            let [templatePage] = await pdfDoc.copyPages(templatePdf, [0]);
            let page = pdfDoc.addPage(templatePage);
            drawCustomFields(page);
            let recordsInCurrentPage = 0;

            for (const record of sortedData) {
                if (recordsInCurrentPage >= rowsPerPage) {
                    [templatePage] = await pdfDoc.copyPages(templatePdf, [0]);
                    page = pdfDoc.addPage(templatePage);
                    drawCustomFields(page);
                    recordsInCurrentPage = 0;
                }

                const yOffset = recordsInCurrentPage * rowHeight;

                for (const [key, value] of Object.entries(mappings)) {
                    if (key === '_config' || key === '_custom') continue;

                    const positions = Array.isArray(value) ? value : [value];

                    positions.forEach(position => {
                        if (!position || position.x === undefined || position.y === undefined) return;

                        let text = '';
                        switch (key) {
                            case 'account_name': text = record.name || ''; break;
                            case 'account_no': text = String(record.customerid || ''); break;
                            case 'meter_no': text = record.meterno || ''; break;
                            case 'address': text = record.address || ''; break;
                            case 'previous_reading': text = String(record.previousreading || '0'); break;
                            case 'sequence_no': text = String(record.sequence || ''); break;
                            case 'barangay': text = record.barangay || ''; break;
                            case 'period': text = formatMonthYear(selectedMonth) || ''; break;
                            case 'period_covered': text = getPeriodCovered(selectedMonth || record.billedmonth) || ''; break;
                            case 'due_date': text = getDueDate(selectedMonth || record.billedmonth) || ''; break;
                            case 'customer_id': text = String(record.customerid || ''); break;
                            case 'customer_name': text = record.name || ''; break;
                            case 'customer_type': text = record.type || ''; break;
                            case 'status': text = record.status || ''; break;
                            case 'current_reading': text = String(record.currentreading || '0'); break;
                            case 'consumption': text = String(record.consumption || '0'); break;
                            case 'billed_month': text = formatMonthYear(record.billedmonth) || ''; break;
                            case 'reader_name': text = assignedReader || ''; break;
                            default: text = '';
                        }

                        text = sanitizeText(text);
                        if (!text) return;

                        const drawY = position.y - yOffset;
                        // if (drawY <= 10) return; // Allow drawing near the bottom edge

                        const size = position.size || 12;
                        const font = getFontForEntry(position);
                        const color = parseColor(position.color);

                        let drawX = position.x;
                        const width = position.width;
                        if (width && position.alignment && font) {
                            const textWidth = font.widthOfTextAtSize(text, size);
                            if (position.alignment === 'center') {
                                drawX = position.x + (width - textWidth) / 2;
                            } else if (position.alignment === 'right') {
                                drawX = position.x + (width - textWidth);
                            }
                        }

                        page.drawText(text, {
                            x: drawX,
                            y: drawY,
                            size,
                            font,
                            color
                        });
                    });
                }
                recordsInCurrentPage++;
            }
        } else {
            for (const record of sortedData) {
                const [templatePage] = await pdfDoc.copyPages(templatePdf, [0]);
                const page = pdfDoc.addPage(templatePage);
                
                drawCustomFields(page);
                
                for (const [key, value] of Object.entries(mappings)) {
                    if (key === '_config' || key === '_custom') continue;

                    const positions = Array.isArray(value) ? value : [value];

                    positions.forEach(position => {
                        if (!position || position.x === undefined || position.y === undefined) return;

                        let text = '';
                        switch (key) {
                            case 'account_name': text = record.name || ''; break;
                            case 'account_no': text = String(record.customerid || ''); break;
                            case 'meter_no': text = record.meterno || ''; break;
                            case 'address': text = record.address || ''; break;
                            case 'previous_reading': text = String(record.previousreading || '0'); break;
                            case 'sequence_no': text = String(record.sequence || ''); break;
                            case 'barangay': text = record.barangay || ''; break;
                            case 'period': text = formatMonthYear(selectedMonth) || ''; break;
                            case 'period_covered': text = getPeriodCovered(selectedMonth || record.billedmonth) || ''; break;
                            case 'due_date': text = getDueDate(selectedMonth || record.billedmonth) || ''; break;
                            case 'customer_id': text = String(record.customerid || ''); break;
                            case 'customer_name': text = record.name || ''; break;
                            case 'customer_type': text = record.type || ''; break;
                            case 'status': text = record.status || ''; break;
                            case 'current_reading': text = String(record.currentreading || '0'); break;
                            case 'consumption': text = String(record.consumption || '0'); break;
                            case 'billed_month': text = formatMonthYear(record.billedmonth) || ''; break;
                            case 'reader_name': text = assignedReader || ''; break;
                            default: text = '';
                        }

                        text = sanitizeText(text);
                        if (!text) return;

                        const size = position.size || 12;
                        const font = getFontForEntry(position);
                        const color = parseColor(position.color);

                        let drawX = position.x;
                        const width = position.width;
                        if (width && position.alignment && font) {
                            const textWidth = font.widthOfTextAtSize(text, size);
                            if (position.alignment === 'center') {
                                drawX = position.x + (width - textWidth) / 2;
                            } else if (position.alignment === 'right') {
                                drawX = position.x + (width - textWidth);
                            }
                        }

                        page.drawText(text, {
                            x: drawX,
                            y: position.y,
                            size,
                            font,
                            color
                        });
                    });
                }
            }
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        return blob;
    } catch (err) {
        throw err;
    }
  };

  const handleGenerateReadingSheets = async () => {
    if (filteredData.length === 0) return;
    setGenerating(true);
    try {
        const blob = await generatePDFBlob();
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        
        let filename = `Reading_Sheets_${selectedMonth}`;
        if (filterBarangay && filterBarangay !== 'all') {
            const safeBarangay = filterBarangay.replace(/[^a-z0-9]/gi, '_').toLowerCase();
            filename += `_${safeBarangay}`;
        } else {
            filename += `_All`;
        }
        filename += `.pdf`;
        
        link.download = filename;
        link.click();
        
        // Clean up
        setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (err) {
        console.error('Error generating reading sheets:', err);
        alert('Error generating reading sheets: ' + err.message);
    } finally {
        setGenerating(false);
    }
  };

  const handlePrintStandardTemplate = async (approver) => {
    if (filteredData.length === 0) return;
    setGenerating(true);
    try {
        const pdfDoc = await PDFDocument.create();
        const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
        const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const safeText = (value) => sanitizeText(value ?? '');
        const approverName = (approver && approver.name) || 'ANTHEA S. FETESIO';
        const approverTitle = (approver && approver.title) || 'PRIVATE SECRETARY/SWDSS-OIC';
        let approverSignature = null;

        if (approver && approver.signatureUrl) {
            try {
                const sigBytes = await fetch(approver.signatureUrl).then(res => res.arrayBuffer());
                const lower = String(approver.signatureUrl).toLowerCase();
                if (lower.includes('.png')) {
                    approverSignature = await pdfDoc.embedPng(sigBytes);
                } else {
                    approverSignature = await pdfDoc.embedJpg(sigBytes);
                }
            } catch (e) {
                console.warn('Signature not found or could not be loaded');
            }
        }
        
        // Load Logo
        let logoImage;
        try {
            const logoBytes = await fetch('/logo-right.png').then(res => res.arrayBuffer());
            logoImage = await pdfDoc.embedPng(logoBytes);
        } catch (e) {
            console.warn('Logo not found or could not be loaded');
        }

        const pageSize = [612, 792];
        const margin = 50;

        for (const record of sortedData) {
            const page = pdfDoc.addPage(pageSize);
            const { width, height } = page.getSize();
            const contentWidth = width - margin * 2;

            // Outer border margin (draw border at the end so it stays on top)
            const borderMargin = 20;

            // Precompute title sizes so header background stops exactly at divider line
            const title1 = 'OFFICE OF THE MUNICIPAL MAYOR';
            const title2 = 'SIBALE WATER DISTRIBUTION AND SANITATION SERVICES';
            const maxTitleWidth = width - margin * 2;

            let officeTitleSize = 13;
            while (fontBold.widthOfTextAtSize(title1, officeTitleSize) > maxTitleWidth && officeTitleSize > 8) {
                officeTitleSize -= 1;
            }
            const title1Width = fontBold.widthOfTextAtSize(title1, officeTitleSize);

            let servicesTitleSize = officeTitleSize;
            while (fontBold.widthOfTextAtSize(title2, servicesTitleSize) > maxTitleWidth && servicesTitleSize > 8) {
                servicesTitleSize -= 1;
            }
            const title2Width = fontBold.widthOfTextAtSize(title2, servicesTitleSize);

            // Compute header background height so it ends above the BILLING STATEMENT title
            const headerHeight = 114 + officeTitleSize;

            // --- Header ---
            let yPos = height - margin;

            // Top blue header background
            page.drawRectangle({
                x: borderMargin,
                y: height - borderMargin - headerHeight,
                width: width - borderMargin * 2,
                height: headerHeight,
                color: rgb(0.78, 0.89, 0.96)
            });

            // Text Header
            page.drawText('Republic of the Philippines', { x: margin, y: yPos, size: 12, font: fontRegular });
            yPos -= 15;
            page.drawText('Province of Romblon', { x: margin, y: yPos, size: 12, font: fontRegular });
            yPos -= 15;
            page.drawText('Municipality of Concepcion', { x: margin, y: yPos, size: 12, font: fontRegular });
            
            // Logo (Top Right)
            if (logoImage) {
                const logoSize = 60;
                page.drawImage(logoImage, {
                    x: width - margin - logoSize,
                    y: height - margin - logoSize + 10,
                    width: logoSize,
                    height: logoSize
                });
            }

            yPos -= 30;
            page.drawText(title1, { x: (width - title1Width) / 2, y: yPos, size: officeTitleSize, font: fontBold });

            yPos -= officeTitleSize + 5;

            page.drawText(title2, { x: (width - title2Width) / 2, y: yPos, size: servicesTitleSize, font: fontBold });

            yPos -= 14;
            page.drawLine({
                start: { x: borderMargin, y: yPos },
                end: { x: width - borderMargin, y: yPos },
                thickness: 1,
                color: rgb(0, 0, 0)
            });

            // BILLING STATEMENT Title
            yPos -= 30;
            const mainTitle = 'BILLING STATEMENT';
            const mainTitleWidth = fontBold.widthOfTextAtSize(mainTitle, 23);
            page.drawText(mainTitle, { 
                x: (width - mainTitleWidth) / 2, 
                y: yPos, 
                size: 23, 
                font: fontBold, 
                color: rgb(0, 0.2, 0.8) // Blue-ish
            });

            // --- Customer Information ---
            yPos -= 34;
            page.drawRectangle({
                x: margin,
                y: yPos - 5,
                width: contentWidth,
                height: 25,
                color: rgb(0.68, 0.85, 0.90), // Light blue
                borderColor: rgb(0, 0, 0),
                borderWidth: 1,
            });
            page.drawText('CUSTOMER INFORMATION', {
                x: margin + 10,
                y: yPos + 4,
                size: 16,
                font: fontBold
            });

            yPos -= 30;
            
            const accountLabel = 'ACCOUNT NO.:';
            const accountLabelSize = 15;
            const accountLabelWidth = fontRegular.widthOfTextAtSize(accountLabel, accountLabelSize);
            page.drawText(accountLabel, { x: margin, y: yPos, size: accountLabelSize, font: fontRegular });
            page.drawText(safeText(record.customerid || ''), {
                x: margin + accountLabelWidth + 8,
                y: yPos,
                size: 17,
                font: fontBold
            });
            
            const typeLabel = 'CUSTOMER TYPE:';
            const typeLabelSize = 15;
            const typeLabelWidth = fontRegular.widthOfTextAtSize(typeLabel, typeLabelSize);
            const typeLabelX = margin + contentWidth / 2;
            page.drawText(typeLabel, { x: typeLabelX, y: yPos, size: typeLabelSize, font: fontRegular });
            page.drawText(safeText(record.type || ''), {
                x: typeLabelX + typeLabelWidth + 8,
                y: yPos,
                size: 17,
                font: fontBold
            });

            yPos -= 26;
            const nameLabel = 'NAME:';
            const nameLabelSize = 15;
            const nameLabelWidth = fontRegular.widthOfTextAtSize(nameLabel, nameLabelSize);
            page.drawText(nameLabel, { x: margin, y: yPos, size: nameLabelSize, font: fontRegular });
            page.drawText(safeText(record.name || ''), {
                x: margin + nameLabelWidth + 8,
                y: yPos,
                size: 17,
                font: fontBold
            });

            yPos -= 26;
            const addressLabel = 'ADDRESS:';
            const addressLabelSize = 15;
            const addressLabelWidth = fontRegular.widthOfTextAtSize(addressLabel, addressLabelSize);
            page.drawText(addressLabel, { x: margin, y: yPos, size: addressLabelSize, font: fontRegular });
            page.drawText(safeText(record.address || ''), {
                x: margin + addressLabelWidth + 8,
                y: yPos,
                size: 16,
                font: fontRegular
            });

            yPos -= 26;
            const barangayLabel = 'BARANGAY:';
            const barangayLabelSize = 15;
            const barangayLabelWidth = fontRegular.widthOfTextAtSize(barangayLabel, barangayLabelSize);
            page.drawText(barangayLabel, { x: margin, y: yPos, size: barangayLabelSize, font: fontRegular });
            page.drawText(safeText(record.barangay || ''), {
                x: margin + barangayLabelWidth + 8,
                y: yPos,
                size: 16,
                font: fontRegular
            });

            // --- Billing Period ---
            yPos -= 34;
            page.drawRectangle({
                x: margin,
                y: yPos - 5,
                width: contentWidth,
                height: 25,
                color: rgb(0.68, 0.85, 0.90),
                borderColor: rgb(0, 0, 0),
                borderWidth: 1,
            });
            page.drawText('BILLING PERIOD', {
                x: margin + 10,
                y: yPos + 4,
                size: 16,
                font: fontBold
            });

            yPos -= 28;
            page.drawText('BILLING MONTH:', { x: margin, y: yPos, size: 15, font: fontRegular });
            page.drawText(getPeriodCovered(selectedMonth) || '', { x: margin + 130, y: yPos, size: 17, font: fontBold });

            yPos -= 26;
            page.drawText('BILL DUE DATE:', { x: margin, y: yPos, size: 15, font: fontRegular });
            page.drawText(getDueDate(selectedMonth) || '', {
                x: margin + 130,
                y: yPos,
                size: 17,
                font: fontBold,
                color: rgb(0.8, 0, 0)
            });

            // --- Meter Reading Details ---
            yPos -= 34;
            page.drawRectangle({
                x: margin,
                y: yPos - 5,
                width: contentWidth,
                height: 25,
                color: rgb(0.68, 0.85, 0.90),
                borderColor: rgb(0, 0, 0),
                borderWidth: 1,
            });
            page.drawText('METER READING DETAILS', {
                x: margin + 10,
                y: yPos + 4,
                size: 16,
                font: fontBold
            });

            yPos -= 28;
            // Table
            const tableTop = yPos;
            const tableWidth = contentWidth;
            const colWidth = tableWidth / 2;
            const rowHeight = 44;

            page.drawRectangle({
                x: margin,
                y: tableTop - rowHeight,
                width: colWidth,
                height: rowHeight,
                borderColor: rgb(0, 0, 0),
                borderWidth: 1,
            });
            page.drawText('PREVIOUS:', { x: margin + 10, y: tableTop - 18, size: 15, font: fontRegular });
            page.drawText(String(record.previousreading || '0'), { x: margin + 110, y: tableTop - 30, size: 20, font: fontBold });

            // Right Cell (Current)
            page.drawRectangle({
                x: margin + colWidth,
                y: tableTop - rowHeight,
                width: colWidth,
                height: rowHeight,
                borderColor: rgb(0, 0, 0),
                borderWidth: 1,
            });
            page.drawText('CURRENT:', { x: margin + colWidth + 10, y: tableTop - 18, size: 15, font: fontRegular });

            const row2Top = tableTop - rowHeight;
            page.drawRectangle({
                x: margin,
                y: row2Top - rowHeight,
                width: colWidth,
                height: rowHeight,
                borderColor: rgb(0, 0, 0),
                borderWidth: 1,
            });
            page.drawText('CONSUMPTION:', { x: margin + 10, y: row2Top - 18, size: 15, font: fontRegular });

            // Right Cell (Amount)
            page.drawRectangle({
                x: margin + colWidth,
                y: row2Top - rowHeight,
                width: colWidth,
                height: rowHeight,
                borderColor: rgb(0, 0, 0),
                borderWidth: 1,
            });
            page.drawText('AMOUNT:', { x: margin + colWidth + 10, y: row2Top - 18, size: 15, font: fontRegular });
            
            yPos = row2Top - rowHeight - 34;

            // --- Footer ---
            page.drawText('READING DATE: __________________________', { x: margin, y: yPos, size: 15, font: fontRegular });
            yPos -= 36;

            page.drawText('READ BY:', { x: margin, y: yPos, size: 15, font: fontRegular });

            const readerLabelWidth = fontRegular.widthOfTextAtSize('READ BY:', 15);
            let readerName = '';
            if (Array.isArray(barangays) && barangays.length > 0) {
                const barangayMatch = barangays.find(b =>
                    String(b.barangay || '').toLowerCase() === String(record.barangay || '').toLowerCase()
                );
                readerName = (barangayMatch?.reader || '').trim();
            }

            if (readerName) {
                page.drawText(safeText(readerName), {
                    x: margin + readerLabelWidth + 12,
                    y: yPos,
                    size: 15,
                    font: fontBold
                });
            } else {
                page.drawText('_______________________________', {
                    x: margin + readerLabelWidth + 12,
                    y: yPos,
                    size: 15,
                    font: fontRegular
                });
            }
            
            yPos -= 26;
            page.drawText('APPROVED BY:', { x: margin, y: yPos, size: 15, font: fontRegular });
            
            yPos -= 56;
            const nameWidth = fontBold.widthOfTextAtSize(approverName, 17);
            const titleWidth = fontRegular.widthOfTextAtSize(approverTitle, 16);
            
            const centerX = width / 2;
            if (approverSignature) {
                const sigWidth = 140;
                const sigHeight = 40;
                page.drawImage(approverSignature, {
                    x: centerX - sigWidth / 2,
                    y: yPos + 8,
                    width: sigWidth,
                    height: sigHeight
                });
            }
            page.drawText(approverName, { x: centerX - (nameWidth / 2), y: yPos, size: 17, font: fontBold });
            page.drawText(approverTitle, { x: centerX - (titleWidth / 2), y: yPos - 16, size: 16, font: fontRegular });
            
            yPos -= 26;
            const footerTopY = borderMargin;
            page.drawRectangle({
                x: borderMargin,
                y: footerTopY,
                width: width - borderMargin * 2,
                height: 40,
                color: rgb(0.68, 0.85, 0.90),
                opacity: 0.5
            });
            const footerText1 = 'For inquiries, please contact or visit the Sibale Water Distribution and Sanitation Services Office during';
            const footerText2 = 'regular office hours (Monday-Friday 8:00Am-5:00PM).';
            
            page.drawText(footerText1, { 
                x: (width - fontRegular.widthOfTextAtSize(footerText1, 12)) / 2, 
                y: footerTopY + 22, 
                size: 12, 
                font: fontRegular 
            });
            page.drawText(footerText2, { 
                x: (width - fontRegular.widthOfTextAtSize(footerText2, 12)) / 2, 
                y: footerTopY + 9, 
                size: 12, 
                font: fontRegular 
            });

            // Draw outer border last so it appears in front of backgrounds
            page.drawRectangle({
                x: borderMargin,
                y: borderMargin,
                width: width - borderMargin * 2,
                height: height - borderMargin * 2,
                borderColor: rgb(0, 0, 0),
                borderWidth: 2
            });
        }

        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        let filename = `Reading_Sheets_Standard_${selectedMonth}`;
        if (filterBarangay && filterBarangay !== 'all') {
          const safeBarangay = filterBarangay.replace(/[^a-z0-9]/gi, '_').toLowerCase();
          filename += `_${safeBarangay}`;
        } else {
          filename += `_all-barangays`;
        }
        link.download = `${filename}.pdf`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 100);

    } catch (err) {
        console.error('Error generating standard sheets:', err);
        alert('Error generating standard sheets: ' + err.message);
    } finally {
        setGenerating(false);
    }
  };

  const handlePrintReadingSheets = () => {
    window.print();
  };

  const exportToExcel = () => {
    const headers = ['Customer ID', 'Name', 'Type', 'Barangay', 'Status', 'Previous Reading', 'Current Reading', 'Consumption', 'Billed Month'];
    const csvContent = [
      headers.join(','),
      ...sortedData.map(item => [
        item.customerid,
        `"${item.name || ''}"`,
        `"${item.type || ''}"`,
        `"${item.barangay || ''}"`,
        `"${item.status || ''}"`,
        item.previousreading || 0,
        item.currentreading || 0,
        item.consumption || 0,
        item.billedmonth || ''
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `reading_sheets_${selectedMonth}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleApproverConfirm = async () => {
    try {
      let approver = null;
      if (approverUsers.length > 0) {
        const fallback = approverUsers[0];
        const selected = approverUsers.find(u => u.userid === selectedApproverId) || fallback;
        const nameParts = [selected.firstname || '', selected.lastname || ''].filter(Boolean);
        const name = nameParts.join(' ') || 'ANTHEA S. FETESIO';
        const title = selected.position || 'PRIVATE SECRETARY/SWDSS-OIC';
        approver = { 
          name, 
          title, 
          signatureUrl: selected.signature_url || null 
        };
      }
      setApproverDialogOpen(false);
      await handlePrintStandardTemplate(approver);
    } catch (err) {
      console.error('Error printing with approver:', err);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <style>{`
        @media print {
          @page {
            margin: 0;
            size: auto;
          }
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
          }
          .hide-on-print {
            display: none !important;
          }
          .show-on-print {
            display: block !important;
          }
          
          /* Minimal Table Styling */
          .MuiTableCell-root {
            padding: 4px 8px !important;
            font-size: 10pt !important;
            border-bottom: 1px solid #ddd !important;
            color: #000 !important;
          }
          .MuiTableHead-root .MuiTableCell-root {
            background-color: transparent !important;
            color: #000 !important;
            font-weight: 900 !important;
            border-bottom: 2px solid #000 !important;
          }
          .MuiPaper-root {
            box-shadow: none !important;
            border: none !important;
          }
        }
        .show-on-print {
          display: none;
        }
      `}</style>
      <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon sx={{ color: '#059669' }} /> Meter Reading Sheets
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate, print, and export batch meter reading sheets with barcode overlays and assigned zone readers.
            </Typography>
          </Box>
          <Button 
            variant="outlined" 
            startIcon={<SettingsIcon />}
            onClick={() => setShowTemplateManager(true)}
            sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, textTransform: 'none' }}
          >
            Manage PDF Template
          </Button>
        </Box>

        {/* Content */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 2.5 }}>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1 }}>
              <Chip 
                icon={<DescriptionIcon sx={{ fontSize: 16 }} />} 
                label={`Total: ${readingData.length} Consumers`} 
                sx={{ bgcolor: '#ecfdf5', color: '#047857', fontWeight: 700, borderRadius: 2 }} 
              />
              {readingData.length !== filteredData.length && (
                <Chip 
                  label={`Filtered: ${filteredData.length}`} 
                  sx={{ bgcolor: '#eff6ff', color: '#0284c7', fontWeight: 700, borderRadius: 2 }} 
                />
              )}
              <Chip icon={<CalendarIcon sx={{ fontSize: 16 }} />} label={selectedMonth ? formatMonthYear(selectedMonth) : 'Current Period'} size="small" sx={{ borderRadius: 2 }} />
            </Stack>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1 }}>
              <Button 
                variant="contained" 
                startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handlePrintMenuOpen}
                disabled={filteredData.length === 0 || generating}
                sx={{ borderRadius: 2.5, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#047857' } }}
              >
                {generating ? 'Preparing Sheets...' : 'Print Reading Sheets'}
              </Button>
              <Menu
                anchorEl={printMenuAnchor}
                open={Boolean(printMenuAnchor)}
                onClose={handlePrintMenuClose}
                PaperProps={{ sx: { borderRadius: 2.5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)' } }}
              >
                <MenuItem onClick={handleCustomPrint} sx={{ fontSize: '0.875rem', fontWeight: 600, py: 1 }}>Print Custom Template (PDF)</MenuItem>
                <MenuItem onClick={handleDownloadStandard} sx={{ fontSize: '0.875rem', fontWeight: 600, py: 1 }}>Print System Standard Template</MenuItem>
              </Menu>
              <Button 
                variant="outlined" 
                startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <PrintIcon />}
                onClick={handlePrintReadingSheets}
                disabled={filteredData.length === 0 || generating}
                sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, textTransform: 'none' }}
              >
                {generating ? 'Preparing...' : 'Print List'}
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<FileDownloadIcon />}
                onClick={exportToExcel}
                disabled={filteredData.length === 0}
                sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, textTransform: 'none' }}
              >
                Export CSV
              </Button>
            </Stack>
          </Box>

          {/* Filters */}
          <Card sx={{ mb: 3, p: 2.5, borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Billed Month"
                  type="month"
                  fullWidth
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  placeholder="Search consumer or meter #..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{ 
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                    sx: { borderRadius: 2.5 }
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Barangay</InputLabel>
                  <Select value={filterBarangay} label="Barangay" onChange={(e) => setFilterBarangay(e.target.value)} sx={{ borderRadius: 2.5 }}>
                    <MenuItem value="all">All Barangays</MenuItem>
                    {barangays.map(b => <MenuItem key={b.barangay} value={b.barangay}>{b.barangay}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Classification</InputLabel>
                  <Select value={filterType} label="Classification" onChange={(e) => setFilterType(e.target.value)} sx={{ borderRadius: 2.5 }}>
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="ResidentialAndCommercial">Commercial & Residential</MenuItem>
                    {customerTypes.map(t => <MenuItem key={t.type} value={t.type}>{t.type}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)} sx={{ borderRadius: 2.5 }}>
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Inactive">Inactive</MenuItem>
                    <MenuItem value="Disconnected">Disconnected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>

          {/* Table */}
          <Card sx={{ borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
            <TableContainer className="print-content">
              <Table size="small">
                <TableHead sx={{ bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0' }}>
                  <TableRow>
                    <TableCell className="hide-on-print" sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'customerid'}
                        direction={sortConfig.key === 'customerid' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('customerid')}
                      >
                        Account ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'name'}
                        direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Account Holder
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'type'}
                        direction={sortConfig.key === 'type' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('type')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'barangay'}
                        direction={sortConfig.key === 'barangay' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('barangay')}
                      >
                        Barangay
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className="hide-on-print" sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'address'}
                        direction={sortConfig.key === 'address' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('address')}
                      >
                        Address
                      </TableSortLabel>
                    </TableCell>
                    <TableCell className="hide-on-print" sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'status'}
                        direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'previousreading'}
                        direction={sortConfig.key === 'previousreading' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('previousreading')}
                      >
                        Prev Reading
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" className="hide-on-print" sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'currentreading'}
                        direction={sortConfig.key === 'currentreading' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('currentreading')}
                      >
                        Curr Reading
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" className="hide-on-print" sx={{ fontWeight: 700, color: '#475569' }}>
                      <TableSortLabel
                        active={sortConfig.key === 'consumption'}
                        direction={sortConfig.key === 'consumption' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('consumption')}
                      >
                        Consumption
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ p: 4 }}><CircularProgress sx={{ color: '#059669' }} size={28} /></TableCell></TableRow>
                  ) : sortedData.length === 0 ? (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ p: 4, color: '#64748b' }}>No meter reading records found for this period</TableCell></TableRow>
                  ) : (
                    sortedData.map(item => (
                      <TableRow key={item.billid || item.customerid} hover sx={{ '&:hover': { bgcolor: '#f8faf9' } }}>
                        <TableCell className="hide-on-print" sx={{ color: '#0f172a', fontWeight: 700, fontFamily: 'monospace' }}>{item.customerid}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{item.name}</TableCell>
                        <TableCell>
                          <span className="hide-on-print"><Chip label={item.type} size="small" sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 600, fontSize: '0.72rem' }} /></span>
                          <span className="show-on-print">{item.type}</span>
                        </TableCell>
                        <TableCell sx={{ color: '#475569' }}>{item.barangay}</TableCell>
                        <TableCell className="hide-on-print" sx={{ color: '#64748b' }}>{item.address || '—'}</TableCell>
                        <TableCell className="hide-on-print">
                          <Chip 
                            label={item.status} 
                            size="small" 
                            sx={{ 
                              bgcolor: item.status === 'Active' ? '#ecfdf5' : '#fee2e2',
                              color: item.status === 'Active' ? '#047857' : '#b91c1c',
                              fontWeight: 700,
                              fontSize: '0.72rem'
                            }} 
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#64748b', fontWeight: 500 }}>{item.previousreading || 0}</TableCell>
                        <TableCell className="hide-on-print" align="right" sx={{ color: '#059669', fontWeight: 700 }}>{item.currentreading || 0}</TableCell>
                        <TableCell className="hide-on-print" align="right" sx={{ fontWeight: 800, color: '#0f172a' }}>{item.consumption || 0} m³</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Box>

        <Dialog 
          fullScreen 
          open={showTemplateManager} 
          onClose={() => setShowTemplateManager(false)} 
          TransitionComponent={Transition}
        >
          <ReadingSheetTemplateManager onClose={() => setShowTemplateManager(false)} />
        </Dialog>

        <Dialog
          open={approverDialogOpen}
          onClose={() => setApproverDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3.5, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Select Approving Officer</DialogTitle>
          <DialogContent dividers>
            {approverLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} sx={{ color: '#059669' }} />
              </Box>
            ) : (
              <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel>Approver</InputLabel>
                <Select
                  label="Approver"
                  value={selectedApproverId}
                  onChange={(e) => setSelectedApproverId(e.target.value)}
                  sx={{ borderRadius: 2.5 }}
                >
                  {approverUsers.map((u) => {
                    const nameParts = [u.firstname || '', u.lastname || ''].filter(Boolean);
                    const name = nameParts.join(' ');
                    const label = u.position ? `${name} - ${u.position}` : name;
                    return (
                      <MenuItem key={u.userid} value={u.userid}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setApproverDialogOpen(false)} sx={{ borderRadius: 2, color: '#64748b', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApproverConfirm}
              disabled={approverLoading || (approverUsers.length > 0 && !selectedApproverId)}
              sx={{ borderRadius: 2, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, '&:hover': { bgcolor: '#047857' } }}
            >
              Confirm & Print
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
};

export default ReadingSheetsPage;
